import './popupRiepilogo.scss';

import React, { useEffect, useImperativeHandle, useState } from "react";
import { LavoriApi } from "../../data";
import useApiConfiguration from "../../apiConfig";
import PieChart, { Series, Tooltip, Legend, Export, Label, Connector } from "devextreme-react/pie-chart";
import { Popup, Position } from "devextreme-react/popup";
import { useScreenSize, useScreenSizeClass } from '../../utils/media-query';
import { List, LoadPanel } from 'devextreme-react';
import { ScrollView } from 'devextreme-react/scroll-view';

export type PopupRiepilogoHandle = {
    show: (idPersonale: number, data: Date) => void
};

type State = {
    idPersonale: number,
    data: Date,
    visible: boolean
};

const PopupRiepilogo: React.ForwardRefRenderFunction<PopupRiepilogoHandle> = (props, ref) => {
    const apiConfiguration = useApiConfiguration();
    const screenSizeClass = useScreenSizeClass();
    const {isXSmall, isSmall} = useScreenSize();

    const [loading, setLoading] = useState(false);
    const [state, setState] = useState<State>({ idPersonale: 0, data: undefined, visible: false });
    const [dataSourceRiepilogo, setDataSourceRiepilogo] = useState<any[]>([]);
    const [showGrafico, setShowGrafico] = useState(false);
    const [oreCaricate, setOreCaricate] = useState(0);
    const [oreMancanti, setOreMancanti] = useState(0);
    const [oreTotali, setOreTotali] = useState(0);
    const [dataSourceGiorniIncompleti, setDataSourceGiorniIncompleti] = useState<any[]>([]);
    const [showGiorniIncompleti, setShowGiorniIncompleti] = useState(false);

    function hideInfo() {
        setState({ idPersonale: 0, visible: false, data: undefined });
    }

    useImperativeHandle(ref, () => ({
        show(idPersonale, data) {
            setLoading(true);
            setState({ idPersonale, data, visible: true });
        }
    }));

    useEffect(() => {
        if (state.visible) {
            const api = new LavoriApi(apiConfiguration);

            const prom1 = api.calcolaOreCommesse({ 
                idPersonale: state.idPersonale,
                data: state.data
            }).then((res) => {
                setDataSourceRiepilogo(res);
                setShowGrafico(res.length > 0);
            });

            const prom2 = api.calcolaTotaleOreMensili({
                idPersonale: state.idPersonale,
                data: state.data
            }).then((res) => {
                setOreCaricate(res.oreTotaliLavorate);
                setOreTotali(res.oreTotaliMese);

                if (res.oreTotaliMese - res.oreTotaliLavorate > 0)
                    setOreMancanti(res.oreTotaliMese - res.oreTotaliLavorate);
                else
                    setOreMancanti(0);
            });

            const prom3 = api.calcolaOreMancanti({
                idPersonale: state.idPersonale,
                data: state.data
            }).then((res) => {
                const resDef = [
                    {
                        key: "Giorni non completi",
                        items: res.filter(x => !x.completo && !x.festivo)
                    }
                ];

                setDataSourceGiorniIncompleti(resDef);
                setShowGiorniIncompleti(resDef[0].items.length > 0);
            });

            Promise.all([prom1, prom2, prom3]).finally(() => {
                setLoading(false);
            });
        }
    }, [state.visible]);

    const customLabelText = (e) => {
        if (e.argumentText.length > 60)
            return `${e.argumentText.substring(0, 60)}...`;

        return `${e.argumentText}`;
    };

    function customTooltipText(arg: any) {
        return {
            text: `${arg.argument} \n ${arg.valueText} ore (${(arg.percent * 100).toFixed(2)}%)`,
        };
    }

    return (
        <React.Fragment>
            <Popup
                visible={state.visible} showTitle={true} showCloseButton={true}
                dragEnabled={false} fullScreen={(isXSmall || isSmall) ? true : false}
                hideOnOutsideClick={true} onHidden={hideInfo}
                title="Riepilogo Mensile"
                container=".dx-viewport"
            >
                {
                    loading ? <LoadPanel style={{ zIndex: 9999 }} position={'center'} visible={true} />
                    : (
                        <ScrollView>
                            <Position at="center" my="center" of="" collision="fit" />

                            {
                                showGrafico && (
                                    <PieChart
                                        type="doughnut"
                                        title="Distribuzione ore"
                                        palette="Soft Pastel"
                                        dataSource={dataSourceRiepilogo}
                                    >
                                        <Series argumentField="descrizione" valueField="valore">
                                            <Label visible={(!isSmall && !isXSmall)} backgroundColor="none" customizeText={customLabelText} textOverflow="ellipsis">
                                                <Connector visible={true} />
                                            </Label>
                                        </Series>
                                        <Export enabled={true} />
                                        <Legend visible={false} />
                                        <Tooltip 
                                            enabled={true} customizeTooltip={customTooltipText} 
                                            paddingLeftRight={10} paddingTopBottom={10} cornerRadius={5} 
                                        />
                                    </PieChart>
                                )
                            }

                            <div className='riepilogo'>
                                <div className={`cards normal ${screenSizeClass}`}>
                                    <div className={'card'}>
                                        <div className='header'>
                                            <div className='title'>Resoconto mensile</div>
                                        </div>
                                        <div className={`content ${screenSizeClass}`}>
                                            <div className='totali'>
                                                <div>
                                                    Ore caricate: <span className='oreCaricate'>{oreCaricate}</span>
                                                </div>
                                                <div>
                                                    Ore mancanti: <span className='oreMancanti'>{oreMancanti}</span>
                                                </div>
                                                <div>
                                                    Ore previste totali: <span>{oreTotali}</span>
                                                </div>
                                            </div>

                                            {
                                                showGiorniIncompleti && (
                                                    <div className='giorniIncompleti'>
                                                        <List 
                                                            dataSource={dataSourceGiorniIncompleti}
                                                            grouped={true}
                                                            showScrollbar='always'
                                                            collapsibleGroups={true}
                                                            hoverStateEnabled={false} selectByClick={false}
                                                            groupRender={(groupData) => (
                                                                <span className='group'>{groupData.key}</span>
                                                            )}
                                                            itemRender={(item) => (
                                                                <div className='item'>
                                                                    <div className={`giorno ${screenSizeClass}`}>
                                                                        {item.giornoString}
                                                                    </div>
                                                                    <div className={`lavorato ${screenSizeClass}`}>
                                                                        <span>{item.oreLavorate}</span>
                                                                    </div>
                                                                    <div className={`daLavorare ${screenSizeClass}`}>
                                                                        <span>{item.oreMancanti}</span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            onContentReady={(e) => {
                                                                var items = e.component.option("items");
                                                                for (var i = 0; i < items.length; i++)
                                                                    e.component.collapseGroup(i);
                                                            }}
                                                        />
                                                    </div>
                                                )
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ScrollView>
                    )
                }
            </Popup>
        </React.Fragment>
    )
}

export default React.forwardRef(PopupRiepilogo);